import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Upload, Check, Sparkles } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import Header from '@/components/layout/Header';

const TalentOnboarding = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    bio: '',
    function: '',
    experience_years: '',
    linkedin_url: '',
    portfolio_url: '',
    resume_url: '',
    availability: '',
  });

  usePageMeta({
    title: 'Complete Your Profile | Talent Collective',
    description: 'Tell us about yourself so we can match you with the right opportunities.'
  });

  const steps = [
    { num: 1, label: 'About You' },
    { num: 2, label: 'Your Expertise' },
    { num: 3, label: 'Your Story' },
    { num: 4, label: 'Availability' },
  ];

  const handleNext = () => {
    if (step === 1 && !formData.name) {
      toast.error('Please enter your full name.');
      return;
    }
    if (step === 2 && !formData.function) {
      toast.error('Please select your primary function.');
      return;
    }
    if (step === 3 && !formData.linkedin_url) {
      toast.error('Please add your LinkedIn URL.');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, resume_url: response.data.url });
      toast.success('Resume uploaded successfully.');
    } catch {
      toast.error('Failed to upload resume.');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/talent/profile`, {
        ...formData,
        experience_years: parseInt(formData.experience_years) || 0,
        availability: parseInt(formData.availability) || 10,
      });
      await refreshProfile();
      toast.success("Welcome aboard! Your profile is ready.");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="onboarding-page">
      <Header />
      <div className="flex items-center justify-center p-4 pt-8 pb-16">
        <div className="absolute top-20 right-20 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
        
        <div className="w-full max-w-2xl relative">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-50 text-teal-700 text-sm font-medium border border-teal-200/50 mb-4">
              <Sparkles className="h-4 w-4" />
              <span>Welcome to the community</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Let us get to know you.</h1>
            <p className="text-gray-600">Tell us about yourself so we can match you with the right opportunities.</p>
          </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8" data-testid="progress-steps">
          {steps.map((s) => (
            <div key={s.num} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                step === s.num ? 'bg-teal-700 text-white shadow-lg shadow-teal-700/25' :
                step > s.num ? 'bg-teal-100 text-teal-700' :
                'bg-gray-100 text-gray-400'
              }`}>
                {step > s.num ? <Check className="h-4 w-4" /> : <span>{s.num}</span>}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {s.num < 4 && <div className={`w-8 h-0.5 ${step > s.num ? 'bg-teal-400' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="p-8 bg-white shadow-xl rounded-2xl" data-testid="onboarding-form">
          {/* Step 1: About You */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-1">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">About You</h2>
                <p className="text-sm text-gray-500">Share some basic details about yourself.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your full name"
                    required
                    className="h-12 rounded-xl"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="headline">Professional Headline</Label>
                  <Input
                    id="headline"
                    value={formData.headline}
                    onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                    placeholder="e.g., Senior Product Manager | Growth & Strategy"
                    className="h-12 rounded-xl"
                    data-testid="headline-input"
                  />
                  <p className="text-xs text-gray-400">Optional, but helps organizations understand your background.</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Your Expertise */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-2">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">Your Expertise</h2>
                <p className="text-sm text-gray-500">Help us understand what you bring to the table.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Primary Function *</Label>
                  <Select value={formData.function} onValueChange={(v) => setFormData({ ...formData, function: v })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="function-select">
                      <SelectValue placeholder="What do you do best?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Product Management</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="engineering">Engineering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Years of Experience *</Label>
                  <Select value={formData.experience_years} onValueChange={(v) => setFormData({ ...formData, experience_years: v })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="experience-select">
                      <SelectValue placeholder="Select your experience range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">0 - 5 years</SelectItem>
                      <SelectItem value="8">5 - 12 years</SelectItem>
                      <SelectItem value="15">12 - 20 years</SelectItem>
                      <SelectItem value="25">20+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Tell us your story</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="What drives you? What kind of impact do you want to create?"
                    className="min-h-[120px] rounded-xl"
                    data-testid="bio-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Your Story */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">Your Story</h2>
                <p className="text-sm text-gray-500">Share links that tell your professional story.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn URL *</Label>
                  <Input
                    id="linkedin"
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/yourprofile"
                    required
                    className="h-12 rounded-xl"
                    data-testid="linkedin-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="portfolio">Portfolio URL</Label>
                  <Input
                    id="portfolio"
                    type="url"
                    value={formData.portfolio_url}
                    onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                    placeholder="https://yourportfolio.com"
                    className="h-12 rounded-xl"
                    data-testid="portfolio-input"
                  />
                  <p className="text-xs text-gray-400">Optional. Share a portfolio, personal site, or any relevant work.</p>
                </div>

                <div className="space-y-2">
                  <Label>Resume</Label>
                  <div className="flex items-center gap-4">
                    {formData.resume_url ? (
                      <div className="flex items-center gap-2 text-sm text-emerald-600">
                        <Check className="h-4 w-4" />
                        Resume uploaded.
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Optional. Upload your resume if you have one handy.</span>
                    )}
                    <label className="cursor-pointer">
                      <Button type="button" variant="outline" size="sm" asChild>
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          {formData.resume_url ? 'Replace' : 'Upload'}
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={handleFileUpload}
                        data-testid="resume-upload"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Availability */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in" data-testid="step-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">Availability</h2>
                <p className="text-sm text-gray-500">Let us know how much time you can dedicate.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Hours available per week</Label>
                  <Select value={formData.availability} onValueChange={(v) => setFormData({ ...formData, availability: v })}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="availability-select">
                      <SelectValue placeholder="Select hours per week" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 hours/week</SelectItem>
                      <SelectItem value="10">10 hours/week</SelectItem>
                      <SelectItem value="15">15 hours/week</SelectItem>
                      <SelectItem value="20">20 hours/week</SelectItem>
                      <SelectItem value="25">25 hours/week</SelectItem>
                      <SelectItem value="30">30 hours/week</SelectItem>
                      <SelectItem value="40">40 hours/week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-teal-50 rounded-xl p-6 border border-teal-200/50">
                  <h3 className="font-semibold text-gray-800 mb-2">What happens next?</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Your profile goes live and you can start browsing projects right away.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Apply to projects that resonate with your skills and values.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>Our team will connect you with organizations once you apply.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={handleBack} className="rounded-full px-6" data-testid="back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <Button type="button" onClick={handleNext} className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 shadow-lg shadow-teal-700/20" data-testid="next-btn">
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-teal-700 hover:bg-teal-800 text-white rounded-full px-8 shadow-lg shadow-teal-700/20"
                data-testid="submit-profile-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating profile...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Complete Profile
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>
            )}
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default TalentOnboarding;
